'use client';

import { useContext, useState, forwardRef, useEffect } from "react";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import {
    faBolt,
    faCoins,
    faChartLine,
    faInfoCircle,
    faChevronDown,
    faChevronUp,
    faSync,
    faExclamationTriangle
} from "@fortawesome/free-solid-svg-icons";
import { motion, AnimatePresence } from "framer-motion";
import { appContext } from "../../App";
import axios from "axios";

interface PriceEstimationComponentProps {
}

interface MiningData {
    coin: string;
    difficulty: number;
    btcPriceInUsd: number;
}

const DEFAULT_VALUES = {
    difficulty: 125864590119490,
    btcPrice: 65000,
};

const PriceEstimationComponent = forwardRef<HTMLDivElement, PriceEstimationComponentProps>((props, ref) => {
    const { darkMode } = useContext(appContext);
    const [hashrate, setHashrate] = useState<string>("100");
    const [btcPrice, setBtcPrice] = useState<string>(DEFAULT_VALUES.btcPrice.toString());
    const [currentApiPrice, setCurrentApiPrice] = useState<number>(DEFAULT_VALUES.btcPrice);
    const [currentDifficulty, setCurrentDifficulty] = useState<number>(DEFAULT_VALUES.difficulty);
    const [showCalculationDetails, setShowCalculationDetails] = useState(false);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState<string | null>(null);
    const [isAutoPrice, setIsAutoPrice] = useState(true);
    const [lastUpdated, setLastUpdated] = useState<string>("");
    const [apiStatus, setApiStatus] = useState<'success' | 'failed' | 'loading'>('loading');

    const isValidMiningData = (data: any): data is MiningData => {
        return data 
            && typeof data === 'object'
            && typeof data.difficulty === 'number' 
            && data.difficulty > 0
            && typeof data.btcPriceInUsd === 'number' 
            && data.btcPriceInUsd > 0;
    };

    // Fetch BTC data from API
    const fetchBtcData = async (isRetry = false) => {
        try {
            setLoading(true);
            setError(null);
            setApiStatus('loading');
            const response = await axios.get<MiningData>("/pool/btc-info");
            const data = response.data;
            
            // Validate the response data
            if (isValidMiningData(data)) {
                setCurrentDifficulty(data.difficulty);
                setCurrentApiPrice(data.btcPriceInUsd);
                
                // Only update the input field if auto mode is enabled
                if (isAutoPrice) {
                    setBtcPrice(data.btcPriceInUsd.toFixed(2));
                }
                
                setApiStatus('success');
                setError(null); // Clear any previous errors
                
                // Format last updated time
                const now = new Date();
                setLastUpdated(now.toLocaleTimeString([], { 
                    hour: '2-digit', 
                    minute: '2-digit',
                    hour12: true 
                }));
            } else {
                // Handle invalid data structure
                console.error("Invalid API response structure:", data);
                throw new Error("Invalid data received from server");
            }
        } catch (err) {
            setApiStatus('failed');
            
            // Determine error message based on error type
            let errorMessage = "Failed to fetch current BTC data. ";
            
            if (axios.isAxiosError(err)) {
                if (err.code === 'ECONNABORTED') {
                    errorMessage += "Request timeout. ";
                } else if (err.response?.status === 404) {
                    errorMessage += "API endpoint not found. ";
                } else if (err.response?.status === 500) {
                    errorMessage += "Server error. ";
                } else if (!err.response) {
                    errorMessage += "Network error. ";
                }
            }
            
            errorMessage += "Using default values for calculations.";
            setError(errorMessage);
            
            console.error("Error fetching BTC data:", err);
            
            // Set default values if we don't have any data yet
            if (!currentDifficulty || currentDifficulty === DEFAULT_VALUES.difficulty) {
                setCurrentDifficulty(DEFAULT_VALUES.difficulty);
                setCurrentApiPrice(DEFAULT_VALUES.btcPrice);
                
                // Only set default BTC price if auto mode is enabled and we don't have a custom price
                if (isAutoPrice && (!btcPrice || btcPrice === DEFAULT_VALUES.btcPrice.toString())) {
                    setBtcPrice(DEFAULT_VALUES.btcPrice.toString());
                }
            }
            
            // Attempt retry logic (optional)
            if (!isRetry) {
                // Try again after 30 seconds
                setTimeout(() => fetchBtcData(true), 30000);
            }
        } finally {
            setLoading(false);
        }
    };

    useEffect(() => {
        fetchBtcData();

        const interval = setInterval(() => {
            fetchBtcData();
        }, 60000);
        
        return () => clearInterval(interval);
    }, []);

    // Corrected mining revenue calculation
    const calculateRevenue = () => {
        const hashNum = parseFloat(hashrate) || 0; // TH/s
        const btcNum = parseFloat(btcPrice) || DEFAULT_VALUES.btcPrice; // USD per BTC
        const difficulty = currentDifficulty || DEFAULT_VALUES.difficulty; // Use current difficulty or fallback
        
        // CORRECT BITCOIN MINING FORMULA:
        // Daily BTC = (Hashrate (TH/s) * 10^12 * 86400 * 6.25) / (Difficulty * 2^32)
        
        const hashesPerSecond = hashNum * Math.pow(10, 12); // Convert TH/s to H/s
        const hashesPerDay = hashesPerSecond * 86400; // Total hashes per day
        const networkHashesPerBlock = difficulty * Math.pow(2, 32); // Network hashes required per block
        
        const blocksPerDay = hashesPerDay / networkHashesPerBlock;
        const dailyBtc = blocksPerDay * 3.125; // BTC per day (post-halving)
        
        const dailyUsd = dailyBtc * btcNum;

        return {
            btc: dailyBtc.toFixed(8),
            usd: dailyUsd,
            usdFormatted: dailyUsd.toLocaleString('en-US', {
                style: 'currency',
                currency: 'USD',
                minimumFractionDigits: 2,
                maximumFractionDigits: 2
            }),
            monthly: dailyUsd * 30,
            yearly: dailyUsd * 365,
            blocksPerDay: blocksPerDay
        };
    };

    const revenue = calculateRevenue();

    // Format difficulty for display
    const formatDifficulty = (difficulty: number) => {
        if (difficulty >= 1e12) {
            return (difficulty / 1e12).toFixed(2) + ' T';
        } else if (difficulty >= 1e9) {
            return (difficulty / 1e9).toFixed(2) + ' G';
        } else if (difficulty >= 1e6) {
            return (difficulty / 1e6).toFixed(2) + ' M';
        }
        return difficulty.toLocaleString();
    };

    // Get display price for top section
    const getDisplayPrice = () => {
        return currentApiPrice || parseFloat(btcPrice) || DEFAULT_VALUES.btcPrice;
    };

    // Get API status indicator
    const getApiStatusIndicator = () => {
        switch (apiStatus) {
            case 'success':
                return (
                    <span className="text-xs px-2 py-1 bg-green-100 dark:bg-green-900/30 text-green-600 dark:text-green-400 rounded">
                        Live Data
                    </span>
                );
            case 'failed':
                return (
                    <span className="text-xs px-2 py-1 bg-yellow-100 dark:bg-yellow-900/30 text-yellow-600 dark:text-yellow-400 rounded flex items-center gap-1">
                        <FontAwesomeIcon icon={faExclamationTriangle} className="w-3 h-3" />
                        Using Defaults
                    </span>
                );
            case 'loading':
                return (
                    <span className="text-xs px-2 py-1 bg-blue-100 dark:bg-blue-900/30 text-blue-600 dark:text-blue-400 rounded animate-pulse">
                        Loading...
                    </span>
                );
        }
    };

    return (
        <section ref={ref} className="min-h-screen w-full bg-gradient-to-b from-white to-blue-50/30 dark:from-slate-950 dark:to-blue-900/10 py-8 md:py-12">
            <div className="container mx-auto px-4 lg:px-6">
                {/* Header outside card */}
                <motion.div
                    className="text-center mb-8 md:mb-12"
                    initial={{ opacity: 0, y: -20 }}
                    animate={{ opacity: 1, y: 0 }}
                >
                    <div className="inline-flex items-center gap-3 mb-4">
                        <div>
                            <h1 className="text-2xl md:text-3xl lg:text-4xl font-bold text-slate-900 dark:text-white">
                                Mining <span className="text-transparent bg-clip-text bg-gradient-to-r from-blue-600 to-cyan-500 dark:from-blue-400 dark:to-cyan-300">Revenue Calculator</span>
                            </h1>
                            <p className="text-slate-600 dark:text-slate-400 mt-2">
                                Estimate your Bitcoin mining earnings with real-time calculations
                            </p>
                        </div>
                    </div>
                </motion.div>

                <div className="max-w-6xl mx-auto">
                    <motion.div
                        className="bg-white dark:bg-slate-900 rounded-2xl md:rounded-3xl shadow-xl border border-slate-200 dark:border-slate-800 p-4 md:p-8 lg:p-10"
                        initial={{ opacity: 0, y: 20 }}
                        animate={{ opacity: 1, y: 0 }}
                    >
                        <div className="space-y-6 md:space-y-8">
                            {/* Current Data Status */}
                            <div className="flex flex-wrap items-center justify-between gap-4 p-4 bg-slate-50 dark:bg-slate-800/50 rounded-xl">
                                <div className="space-y-2">
                                    <div className="flex items-center gap-2">
                                        <span className="text-sm font-medium text-slate-600 dark:text-slate-400">
                                            Current Network Data
                                        </span>
                                        {getApiStatusIndicator()}
                                    </div>
                                    <div className="flex flex-wrap gap-4 md:gap-6">
                                        <div>
                                            <div className="text-xs text-slate-500 dark:text-slate-500">Network Difficulty</div>
                                            <div className="text-lg font-semibold text-slate-900 dark:text-white">
                                                {loading ? '...' : formatDifficulty(currentDifficulty)}
                                                {apiStatus === 'failed' && (
                                                    <span className="ml-2 text-xs text-yellow-500">(default)</span>
                                                )}
                                            </div>
                                        </div>
                                        <div>
                                            <div className="text-xs text-slate-500 dark:text-slate-500">Market BTC Price</div>
                                            <div className="text-lg font-semibold text-green-600 dark:text-green-400">
                                                {loading ? '...' : `$${getDisplayPrice().toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`}
                                                {apiStatus === 'failed' && (
                                                    <span className="ml-2 text-xs text-yellow-500">(default)</span>
                                                )}
                                            </div>
                                        </div>
                                    </div>
                                    {apiStatus === 'failed' && (
                                        <p className="text-xs text-yellow-600 dark:text-yellow-500 mt-2">
                                            Using default values. Data will automatically retry every 60 seconds.
                                        </p>
                                    )}
                                </div>
                                <button
                                    onClick={() => fetchBtcData()}
                                    disabled={loading}
                                    className="flex items-center gap-2 px-4 py-2 bg-blue-500 hover:bg-blue-600 text-white rounded-lg transition-colors disabled:opacity-50 disabled:cursor-not-allowed text-sm"
                                >
                                    <FontAwesomeIcon icon={faSync} className={`w-4 h-4 ${loading ? 'animate-spin' : ''}`} />
                                    {loading ? 'Updating...' : 'Refresh Data'}
                                </button>
                            </div>

                            {/* Error Message Display */}
                            {error && (
                                <motion.div
                                    initial={{ opacity: 0, y: -10 }}
                                    animate={{ opacity: 1, y: 0 }}
                                    className="bg-yellow-50 dark:bg-yellow-900/20 border border-yellow-200 dark:border-yellow-800/30 rounded-lg p-4"
                                >
                                    <div className="flex items-center gap-3">
                                        <FontAwesomeIcon icon={faExclamationTriangle} className="text-yellow-500" />
                                        <div className="text-sm text-yellow-700 dark:text-yellow-400">{error}</div>
                                    </div>
                                </motion.div>
                            )}

                            {/* Calculator Section */}
                            <div className="grid lg:grid-cols-2 gap-6 md:gap-8">
                                {/* Inputs Column */}
                                <div className="space-y-6">
                                    {/* Hashrate Input */}
                                    <div className="space-y-3">
                                        <label className="flex items-center justify-between text-sm font-semibold text-slate-700 dark:text-slate-300">
                                            <span className="flex items-center gap-2">
                                                <FontAwesomeIcon icon={faBolt} className="text-blue-500" />
                                                Hashrate (TH/s)
                                            </span>
                                            <span className="text-xs px-2 py-1 bg-blue-100 dark:bg-blue-900/30 text-blue-600 dark:text-blue-400 rounded">BTC</span>
                                        </label>
                                        <div className="relative">
                                            <input
                                                type="number"
                                                value={hashrate}
                                                onChange={(e) => setHashrate(e.target.value)}
                                                className="w-full px-4 py-3 md:px-5 md:py-4 bg-slate-50 dark:bg-slate-800 text-slate-900 dark:text-white rounded-lg border border-slate-300 dark:border-slate-700 focus:border-blue-500 focus:ring-2 focus:ring-blue-500/20 outline-none text-lg"
                                                step="1"
                                                min="0"
                                                placeholder="Enter hashrate"
                                            />
                                            <div className="absolute right-3 top-1/2 transform -translate-y-1/2">
                                                <span className="text-xs text-slate-500">TH/s</span>
                                            </div>
                                        </div>
                                    </div>

                                    {/* BTC Price Input with Toggle */}
                                    <div className="space-y-3">
                                        <div className="flex items-center justify-between">
                                            <label className="flex items-center gap-2 text-sm font-semibold text-slate-700 dark:text-slate-300">
                                                <FontAwesomeIcon icon={faCoins} className="text-yellow-500" />
                                                BTC Price for Calculation
                                            </label>
                                            <div className="flex items-center gap-2">
                                                <span className="text-xs text-slate-500 dark:text-slate-400">
                                                    {isAutoPrice ? 'Auto' : 'Manual'}
                                                </span>
                                                <button
                                                    onClick={() => {
                                                        if (isAutoPrice) {
                                                            // Switching to manual - keep current value
                                                            setIsAutoPrice(false);
                                                        } else {
                                                            // Switching to auto - use API price if available, otherwise default
                                                            setIsAutoPrice(true);
                                                            setBtcPrice((currentApiPrice || DEFAULT_VALUES.btcPrice).toFixed(2));
                                                        }
                                                    }}
                                                    className={`relative inline-flex h-6 w-11 items-center rounded-full transition-colors ${isAutoPrice ? 'bg-green-500' : 'bg-slate-300 dark:bg-slate-700'}`}
                                                >
                                                    <span className={`inline-block h-4 w-4 transform rounded-full bg-white transition-transform ${isAutoPrice ? 'translate-x-6' : 'translate-x-1'}`} />
                                                </button>
                                            </div>
                                        </div>
                                        <div className="relative">
                                            <input
                                                type="number"
                                                value={btcPrice}
                                                onChange={(e) => {
                                                    setBtcPrice(e.target.value);
                                                    setIsAutoPrice(false);
                                                }}
                                                disabled={loading && isAutoPrice}
                                                className={`w-full px-4 py-3 md:px-5 md:py-4 bg-slate-50 dark:bg-slate-800 text-slate-900 dark:text-white rounded-lg border ${isAutoPrice ? 'border-green-300 dark:border-green-700' : 'border-slate-300 dark:border-slate-700'} focus:border-blue-500 focus:ring-2 focus:ring-blue-500/20 outline-none text-lg ${loading && isAutoPrice ? 'opacity-50 cursor-not-allowed' : ''}`}
                                                step="0.01"
                                                min="0"
                                                placeholder={loading && isAutoPrice ? "Loading..." : "Enter BTC price"}
                                            />
                                            <div className="absolute right-3 top-1/2 transform -translate-y-1/2 flex items-center gap-2">
                                                <span className="text-xs text-slate-500">USD</span>
                                                {isAutoPrice && (
                                                    <span className={`text-xs px-1.5 py-0.5 rounded ${
                                                        apiStatus === 'success' 
                                                            ? 'bg-green-100 dark:bg-green-900/30 text-green-600 dark:text-green-400'
                                                            : 'bg-yellow-100 dark:bg-yellow-900/30 text-yellow-600 dark:text-yellow-400'
                                                    }`}>
                                                        {apiStatus === 'success' ? 'Live' : 'Default'}
                                                    </span>
                                                )}
                                            </div>
                                        </div>
                                        <div className="flex items-center justify-between">
                                            <p className={`text-xs ${isAutoPrice 
                                                ? apiStatus === 'success' 
                                                    ? 'text-green-600 dark:text-green-400' 
                                                    : 'text-yellow-600 dark:text-yellow-400'
                                                : 'text-blue-600 dark:text-blue-400'}`}>
                                                {isAutoPrice 
                                                    ? apiStatus === 'success'
                                                        ? 'Using current market price'
                                                        : 'Using default price (API unavailable)'
                                                    : 'Using custom price for calculation'}
                                            </p>
                                            {!isAutoPrice && (currentApiPrice > 0 || DEFAULT_VALUES.btcPrice > 0) && (
                                                <button
                                                    onClick={() => {
                                                        setBtcPrice((currentApiPrice || DEFAULT_VALUES.btcPrice).toFixed(2));
                                                        setIsAutoPrice(true);
                                                    }}
                                                    className="text-xs text-blue-500 hover:text-blue-600 dark:text-blue-400 dark:hover:text-blue-300 underline"
                                                >
                                                    Use {apiStatus === 'success' ? 'market' : 'default'} price
                                                </button>
                                            )}
                                        </div>
                                    </div>

                                    {/* Calculation Details Accordion */}
                                    <div className="border border-slate-300 dark:border-slate-700 rounded-xl overflow-hidden">
                                        <button
                                            onClick={() => setShowCalculationDetails(!showCalculationDetails)}
                                            className="w-full flex items-center justify-between p-4 bg-slate-50 dark:bg-slate-800 hover:bg-slate-100 dark:hover:bg-slate-700 transition-colors"
                                        >
                                            <div className="flex items-center gap-3">
                                                <FontAwesomeIcon icon={faInfoCircle} className="text-blue-500 w-4 h-4" />
                                                <span className="text-sm md:text-base font-semibold text-slate-900 dark:text-white">
                                                    How is this calculated?
                                                </span>
                                            </div>
                                            <FontAwesomeIcon
                                                icon={showCalculationDetails ? faChevronUp : faChevronDown}
                                                className="text-slate-500 w-4 h-4"
                                            />
                                        </button>

                                        <AnimatePresence>
                                            {showCalculationDetails && (
                                                <motion.div
                                                    initial={{ opacity: 0, height: 0 }}
                                                    animate={{ opacity: 1, height: 'auto' }}
                                                    exit={{ opacity: 0, height: 0 }}
                                                    transition={{ duration: 0.2 }}
                                                >
                                                    <div className="p-4 border-t border-slate-300 dark:border-slate-700 bg-white dark:bg-slate-900">
                                                        <div className="space-y-3">
                                                            <p className="text-sm text-slate-600 dark:text-slate-400">
                                                                Estimates are based on the FPPS payout scheme and current network difficulty.
                                                                The calculation formula includes:
                                                            </p>
                                                            <ul className="space-y-2 text-sm text-slate-600 dark:text-slate-400">
                                                                <li className="flex items-start gap-2">
                                                                    <div className="w-1.5 h-1.5 rounded-full bg-blue-500 mt-1.5"></div>
                                                                    <span>
                                                                        Daily BTC = (Hashrate × 10<sup>12</sup> × 86,400 × 3.125) ÷ (Difficulty × 2<sup>32</sup>)
                                                                    </span>
                                                                </li>
                                                                <li className="flex items-start gap-2">
                                                                    <div className="w-1.5 h-1.5 rounded-full bg-blue-500 mt-1.5"></div>
                                                                    <span>Where 1 TH/s = 1,000,000,000,000 hashes per second</span>
                                                                </li>
                                                                <li className="flex items-start gap-2">
                                                                    <div className="w-1.5 h-1.5 rounded-full bg-blue-500 mt-1.5"></div>
                                                                    <span>USD Value = Daily BTC × {isAutoPrice ? (apiStatus === 'success' ? 'Market' : 'Default') : 'Custom'} BTC Price</span>
                                                                </li>
                                                                <li className="flex items-start gap-2">
                                                                    <div className="w-1.5 h-1.5 rounded-full bg-blue-500 mt-1.5"></div>
                                                                    <span>Current Network Difficulty: {formatDifficulty(currentDifficulty)} {apiStatus === 'failed' && '(default)'}</span>
                                                                </li>
                                                                <li className="flex items-start gap-2">
                                                                    <div className="w-1.5 h-1.5 rounded-full bg-blue-500 mt-1.5"></div>
                                                                    <span>Block Reward: 3.125 BTC (post-halving)</span>
                                                                </li>
                                                            </ul>
                                                            <div className="bg-slate-50 dark:bg-slate-800/50 p-3 rounded-lg">
                                                                <div className="text-xs font-medium text-slate-700 dark:text-slate-300 mb-1">Example Calculation:</div>
                                                                <div className="text-xs text-slate-600 dark:text-slate-400 space-y-1">
                                                                    <div>For 100 TH/s with current difficulty:</div>
                                                                    <div>Daily BTC = (100 × 1e12 × 86400 × 3.125) ÷ ({currentDifficulty.toExponential(2)} × 4.295e9)</div>
                                                                    <div>≈ {revenue.btc} BTC/day</div>
                                                                </div>
                                                            </div>
                                                            <p className="text-xs text-slate-500 dark:text-slate-500 pt-2 border-t border-slate-300/30 dark:border-slate-700/30">
                                                                Note: Estimates are for reference only. Actual earnings may vary based on network conditions, 
                                                                pool luck, and transaction fees. All calculations exclude electricity and operational costs.
                                                            </p>
                                                        </div>
                                                    </div>
                                                </motion.div>
                                            )}
                                        </AnimatePresence>
                                    </div>
                                </div>

                                {/* Results Column */}
                                <div className="space-y-6">
                                    {/* Main Results Card */}
                                    <motion.div
                                        className="bg-gradient-to-br from-blue-50 to-cyan-50 dark:from-blue-900/20 dark:to-cyan-900/10 rounded-2xl p-6 border border-blue-200/50 dark:border-blue-800/30"
                                        initial={{ scale: 0.95 }}
                                        animate={{ scale: 1 }}
                                        transition={{ type: "spring", delay: 0.1 }}
                                    >
                                        <div className="flex items-center justify-between mb-6">
                                            <div className="flex items-center gap-3">
                                                <FontAwesomeIcon icon={faChartLine} className="text-blue-500 w-5 h-5" />
                                                <h2 className="text-lg md:text-xl font-semibold text-slate-900 dark:text-white">
                                                    Estimated Daily Revenue
                                                </h2>
                                            </div>
                                            {apiStatus === 'failed' && (
                                                <span className="text-xs px-2 py-1 bg-yellow-100 dark:bg-yellow-900/30 text-yellow-600 dark:text-yellow-400 rounded">
                                                    Estimated
                                                </span>
                                            )}
                                        </div>

                                        <div className="space-y-6">
                                            <div className="grid md:grid-cols-2 gap-4 md:gap-6">
                                                <div className="space-y-2">
                                                    <div className="text-sm text-slate-600 dark:text-slate-400">Bitcoin Earnings</div>
                                                    <div className="text-2xl md:text-3xl lg:text-4xl font-bold text-slate-900 dark:text-white">
                                                        {revenue.btc}
                                                    </div>
                                                    <div className="text-xs md:text-sm text-slate-500 dark:text-slate-500">BTC per day</div>
                                                </div>
                                                <div className="space-y-2">
                                                    <div className="text-sm text-slate-600 dark:text-slate-400">USD Value</div>
                                                    <div className="text-2xl md:text-3xl lg:text-4xl font-bold text-blue-600 dark:text-blue-400">
                                                        {revenue.usdFormatted}
                                                    </div>
                                                    <div className="text-xs md:text-sm text-slate-500 dark:text-slate-500">
                                                        @ ${parseFloat(btcPrice || DEFAULT_VALUES.btcPrice.toString()).toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}/BTC
                                                        {apiStatus === 'failed' && isAutoPrice && ' (default)'}
                                                    </div>
                                                </div>
                                            </div>
                                        </div>
                                    </motion.div>
                                </div>
                            </div>

                            {/* Disclaimer */}
                            <div className="pt-4 border-t border-slate-300 dark:border-slate-700">
                                <p className="text-xs text-slate-500 dark:text-slate-500 text-center">
                                    * This calculator provides estimates for reference only. Actual mining revenue depends on
                                    network difficulty, pool performance, and market volatility. Past performance does not
                                    guarantee future results. All calculations exclude electricity costs and are based on
                                    {apiStatus === 'success' 
                                        ? ` current network difficulty of ${formatDifficulty(currentDifficulty)}.`
                                        : ` default network values. API data is currently unavailable.`}
                                    {apiStatus === 'failed' && ' Using fallback values until connection is restored.'}
                                    {' '}Market data updates every 60 seconds.
                                </p>
                            </div>
                        </div>
                    </motion.div>
                </div>
            </div>
        </section>
    );
});

export default PriceEstimationComponent;